// SupplierProfilePage.tsx - Supplier Self-Service Dashboard
// Add this component to App.tsx and include in the page routing

import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

interface DateRange {
  startDate: string;
  endDate: string;
}

interface SupplierProfileData {
  supplier_name: string;
  performance: {
    total_orders: number;
    total_claims: number;
    delivery_rate: number;
    cancellation_rate: number;
    avg_delivery_days: number;
    on_time_rate: number;
  };
  pipeline: { status: string; count: number; value: number }[];
  revenue: {
    total_delivered: number;
    avg_order_value: number;
    total_quoted: number;
  };
  revenue_trend: { week: string; orders: number; value: number }[];
  win_rate: { quotes: number; won: number; rate: number };
  competitive_position: {
    part_type: string;
    my_avg: number;
    market_avg: number;
    my_orders: number;
    competitors: number;
    diff_pct: number;
  }[];
  ranking: { rank: number | null; total: number };
  late_deliveries: {
    claim_number: string;
    part_name: string;
    price: number;
    deadline: string;
    delivered: string;
    days_late: number;
  }[];
  cancelled_orders: {
    claim_number: string;
    part_name: string;
    price: number;
    order_date: string;
    workshop: string;
  }[];
  top_workshops: { name: string; orders: number; value: number; rate: number }[];
  top_insurers: { name: string; orders: number; value: number }[];
  top_parts: { name: string; type: string; orders: number; value: number; avg_price: number }[];
  part_type_demand: { type: string; orders: number; value: number }[];
  sla_adherence: { bucket: string; count: number }[];
}

const formatCurrency = (value: number) => 
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const formatNumber = (value: number) => 
  new Intl.NumberFormat('en-US').format(value);

const truncate = (str: string, len: number) => 
  str && str.length > len ? str.substring(0, len) + '...' : str;

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
const SLA_COLORS: Record<string, string> = {
  'On Time': '#22c55e',
  'Late': '#ef4444',
  'Pending': '#f59e0b',
  'No Deadline': '#94a3b8'
};

const SupplierProfilePage: React.FC<{ dateRange: DateRange; authFetch: (url: string, options?: RequestInit) => Promise<Response> }> = ({ dateRange, authFetch }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SupplierProfileData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'competitive' | 'problems' | 'customers' | 'demand'>('overview');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (dateRange.startDate) params.append('startDate', dateRange.startDate);
        if (dateRange.endDate) params.append('endDate', dateRange.endDate);
        
        const res = await authFetch(`/api/supplier/profile?${params}`);
        const json = await res.json();
        
        if (json.success) {
          setData(json.data);
        } else {
          setError(json.error || 'Failed to load supplier data');
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange, authFetch]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading supplier profile...</div>;
  if (error) return <div className="bg-red-50 border border-red-200 rounded p-4 text-red-800">{error}</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data available</div>;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'competitive', label: 'Competitive Position' },
    { key: 'problems', label: 'Problem Areas' },
    { key: 'customers', label: 'Top Customers' },
    { key: 'demand', label: 'Part Demand' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{data.supplier_name}</h1>
            <p className="text-sm text-gray-500">Supplier Performance Dashboard</p>
          </div>
          {data.ranking.rank && (
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">#{data.ranking.rank}</div>
              <div className="text-sm text-gray-500">of {data.ranking.total} suppliers</div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                activeTab === tab.key 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Performance KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{formatNumber(data.performance.total_orders)}</div>
              <div className="text-sm text-gray-500">Total Orders</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{data.performance.delivery_rate}%</div>
              <div className="text-sm text-gray-500">Delivery Rate</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{data.performance.cancellation_rate}%</div>
              <div className="text-sm text-gray-500">Cancellation Rate</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">{data.performance.avg_delivery_days}</div>
              <div className="text-sm text-gray-500">Avg Delivery Days</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-teal-600">{data.performance.on_time_rate || 0}%</div>
              <div className="text-sm text-gray-500">On-Time Rate</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-3xl font-bold text-orange-600">{data.win_rate.rate}%</div>
              <div className="text-sm text-gray-500">Win Rate</div>
            </div>
          </div>

          {/* Revenue & Pipeline Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Revenue */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Revenue Summary</h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-green-50 rounded">
                  <div className="text-xl font-bold text-green-600">{formatCurrency(data.revenue.total_delivered)}</div>
                  <div className="text-xs text-gray-500">Delivered Value</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded">
                  <div className="text-xl font-bold text-blue-600">{formatCurrency(data.revenue.avg_order_value)}</div>
                  <div className="text-xs text-gray-500">Avg Order Value</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="text-xl font-bold text-gray-600">{formatCurrency(data.revenue.total_quoted)}</div>
                  <div className="text-xs text-gray-500">Total Quoted</div>
                </div>
              </div>
            </div>

            {/* Pipeline */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Order Pipeline</h3>
              <div className="grid grid-cols-2 gap-3">
                {data.pipeline.map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm capitalize">{p.status.replace('_', ' ')}</span>
                    <span className="font-bold">{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Revenue Trend Chart */}
          {data.revenue_trend.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Revenue Trend (Weekly)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(v) => v.substring(5, 10)} />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Bar dataKey="value" fill="#22c55e" name="Delivered Value" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* SLA Adherence */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-4">Delivery SLA Adherence</h3>
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={data.sla_adherence}
                    dataKey="count"
                    nameKey="bucket"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ bucket, count }) => `${bucket}: ${count}`}
                  >
                    {data.sla_adherence.map((entry, index) => (
                      <Cell key={index} fill={SLA_COLORS[entry.bucket] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Competitive Position Tab */}
      {activeTab === 'competitive' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Price Competitiveness by Part Type</h3>
              <p className="text-sm text-gray-500">Negative diff = below market (competitive), Positive = above market</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Part Type</th>
                    <th className="px-4 py-2 text-right">My Avg Price</th>
                    <th className="px-4 py-2 text-right">Market Avg</th>
                    <th className="px-4 py-2 text-right">Difference</th>
                    <th className="px-4 py-2 text-right">My Orders</th>
                    <th className="px-4 py-2 text-right">Competitors</th>
                    <th className="px-4 py-2 text-center">Position</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.competitive_position.map((c, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{truncate(c.part_type, 25)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(c.my_avg)}</td>
                      <td className="px-4 py-2 text-right text-gray-500">{formatCurrency(c.market_avg)}</td>
                      <td className={`px-4 py-2 text-right font-bold ${c.diff_pct < 0 ? 'text-green-600' : c.diff_pct > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                        {c.diff_pct > 0 ? '+' : ''}{c.diff_pct}%
                      </td>
                      <td className="px-4 py-2 text-right">{c.my_orders}</td>
                      <td className="px-4 py-2 text-right">{c.competitors}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          c.diff_pct < -5 ? 'bg-green-100 text-green-800' :
                          c.diff_pct > 5 ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {c.diff_pct < -5 ? 'Competitive' : c.diff_pct > 5 ? 'Above Market' : 'At Market'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Win Rate Summary */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-4">Quote-to-Order Conversion</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded">
                <div className="text-3xl font-bold text-blue-600">{data.win_rate.quotes}</div>
                <div className="text-sm text-gray-500">Quotes Submitted</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded">
                <div className="text-3xl font-bold text-green-600">{data.win_rate.won}</div>
                <div className="text-sm text-gray-500">Orders Won</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded">
                <div className="text-3xl font-bold text-purple-600">{data.win_rate.rate}%</div>
                <div className="text-sm text-gray-500">Conversion Rate</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Problem Areas Tab */}
      {activeTab === 'problems' && (
        <div className="space-y-6">
          {/* Late Deliveries */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b bg-red-50">
              <h3 className="font-semibold text-red-800">Late Deliveries ({data.late_deliveries.length})</h3>
            </div>
            {data.late_deliveries.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Claim</th>
                      <th className="px-4 py-2 text-left">Part</th>
                      <th className="px-4 py-2 text-right">Price</th>
                      <th className="px-4 py-2 text-center">Deadline</th>
                      <th className="px-4 py-2 text-center">Delivered</th>
                      <th className="px-4 py-2 text-center">Days Late</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.late_deliveries.map((d, i) => (
                      <tr key={i} className="hover:bg-red-50">
                        <td className="px-4 py-2 font-medium">{truncate(d.claim_number, 15)}</td>
                        <td className="px-4 py-2">{truncate(d.part_name, 25)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(d.price)}</td>
                        <td className="px-4 py-2 text-center text-gray-500">{d.deadline?.substring(0, 10)}</td>
                        <td className="px-4 py-2 text-center">{d.delivered?.substring(0, 10)}</td>
                        <td className="px-4 py-2 text-center">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-bold">{d.days_late}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">No late deliveries</div>
            )}
          </div>

          {/* Cancelled Orders */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b bg-orange-50">
              <h3 className="font-semibold text-orange-800">Cancelled Orders ({data.cancelled_orders.length})</h3>
            </div>
            {data.cancelled_orders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Claim</th>
                      <th className="px-4 py-2 text-left">Part</th>
                      <th className="px-4 py-2 text-left">Workshop</th>
                      <th className="px-4 py-2 text-right">Price</th>
                      <th className="px-4 py-2 text-center">Order Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.cancelled_orders.map((c, i) => (
                      <tr key={i} className="hover:bg-orange-50">
                        <td className="px-4 py-2 font-medium">{truncate(c.claim_number, 15)}</td>
                        <td className="px-4 py-2">{truncate(c.part_name, 25)}</td>
                        <td className="px-4 py-2">{truncate(c.workshop, 20)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(c.price)}</td>
                        <td className="px-4 py-2 text-center">{c.order_date?.substring(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">No cancelled orders</div>
            )}
          </div>
        </div>
      )}

      {/* Top Customers Tab */}
      {activeTab === 'customers' && (
        <div className="space-y-6">
          {/* Top Workshops */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Top Workshops by Value</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Workshop</th>
                    <th className="px-4 py-2 text-right">Orders</th>
                    <th className="px-4 py-2 text-right">Delivered Value</th>
                    <th className="px-4 py-2 text-right">Delivery Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.top_workshops.map((w, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{truncate(w.name, 30)}</td>
                      <td className="px-4 py-2 text-right">{w.orders}</td>
                      <td className="px-4 py-2 text-right text-green-600 font-medium">{formatCurrency(w.value)}</td>
                      <td className="px-4 py-2 text-right">{w.rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Insurers */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Top Insurers by Value</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Insurer</th>
                    <th className="px-4 py-2 text-right">Orders</th>
                    <th className="px-4 py-2 text-right">Delivered Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.top_insurers.map((ins, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{truncate(ins.name, 30)}</td>
                      <td className="px-4 py-2 text-right">{ins.orders}</td>
                      <td className="px-4 py-2 text-right text-green-600 font-medium">{formatCurrency(ins.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Workshop Relationship Chart */}
          {data.top_workshops.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Workshop Relationship Strength</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.top_workshops.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Bar dataKey="value" fill="#3b82f6" name="Delivered Value" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Part Demand Tab */}
      {activeTab === 'demand' && (
        <div className="space-y-6">
          {/* Part Type Demand Chart */}
          {data.part_type_demand.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-4">Demand by Part Type</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.part_type_demand}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="orders" fill="#3b82f6" name="Order Count" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top Parts Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Most Ordered Parts</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Part</th>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-right">Orders</th>
                    <th className="px-4 py-2 text-right">Total Value</th>
                    <th className="px-4 py-2 text-right">Avg Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.top_parts.map((p, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{truncate(p.name, 30)}</td>
                      <td className="px-4 py-2 text-gray-500">{truncate(p.type, 20)}</td>
                      <td className="px-4 py-2 text-right font-bold">{p.orders}</td>
                      <td className="px-4 py-2 text-right text-green-600">{formatCurrency(p.value)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(p.avg_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierProfilePage;